'use client'
import React, { useState } from 'react';
import axios from 'axios';
import { FaInstagram, FaFacebook, FaEnvelope, FaYoutube } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const ContactPage: React.FC = () => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            await axios.post('/api/contact', { name, email, message });
            setName('');
            setEmail('');
            setMessage('');
            setSubmitted(true);
        } catch (error) {
            console.error('Error submitting contact form:', error);
            setError('Failed to submit the form. Please try again.');
        }

        setSubmitting(false);
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { 
            opacity: 1,
            transition: { 
                duration: 1.2, 
                ease: "easeInOut",
                when: "beforeChildren",
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: { 
            y: 0, 
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const socialIconVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: { 
            scale: 1, 
            opacity: 1,
            transition: { 
                duration: 0.4,
                ease: "easeOut"
            } 
        },
        hover: { 
            scale: 1.1,
            transition: { duration: 0.2 }
        }
    };

    const formControlVariants = {
        hidden: { x: -10, opacity: 0 },
        visible: { 
            x: 0, 
            opacity: 1,
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        }
    };

    return (
        <motion.div 
            className="py-8 sm:py-16 font-goodfont"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
        >
            <div className="container mx-auto px-4">
                <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl text-white mb-6 sm:mb-8 text-center uppercase tracking-widest">
                    Contact
                </motion.h1>
                <motion.hr 
                    variants={itemVariants} 
                    className="border-gray-600 mt-4 sm:mt-5 mb-6 sm:mb-9 w-4/5 sm:w-2/6 m-auto"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.8, ease: "easeInOut" }}
                />
                <motion.div variants={itemVariants} className="mt-6 sm:mt-8 text-center">
                    <div className="flex flex-wrap justify-center space-x-4 sm:space-x-6 items-center">
                        {[
                            { icon: FaInstagram, href: "https://www.instagram.com/johnny.danz.music" },
                            { icon: FaFacebook, href: "https://m.facebook.com/john.danziger.96?mibextid=LQQJ4d" },
                            { icon: FaYoutube, href: "https://www.youtube.com/channel/UCimPY4pjI-pAQkZsITQkiaA" },
                            { icon: FaEnvelope, href: null, text: "info@johndanziger.com" }
                        ].map((social, index) => (
                            <motion.div key={index} variants={socialIconVariants} whileHover="hover">
                                {social.href ? (
                                    <a href={social.href} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors duration-300">
                                        <social.icon size={20} className="sm:w-6 sm:h-6" />
                                    </a>
                                ) : (
                                    <div className="flex items-center text-gray-400">
                                        <social.icon size={20} className="mr-2 sm:w-6 sm:h-6" />
                                        <span className="text-sm sm:text-base">{social.text}</span>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
                <motion.div 
                    variants={itemVariants}
                    className="bg-black rounded-none overflow-hidden border border-white mt-6 sm:mt-8"
                >
                    <div className="p-4 sm:p-8">
                        <form onSubmit={handleSubmit}>
                            {['name', 'email', 'message'].map((field) => (
                                <motion.div key={field} className="mb-4 sm:mb-6" variants={formControlVariants}>
                                    <label htmlFor={field} className="block text-gray-400 text-base sm:text-lg mb-2">
                                        {field.charAt(0).toUpperCase() + field.slice(1)}
                                    </label>
                                    {field === 'message' ? (
                                        <motion.textarea
                                            id={field}
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            required
                                            rows={5}
                                            className="w-full px-3 sm:px-4 py-2 bg-black text-white border-b border-gray-600 focus:outline-none focus:border-white"
                                            whileHover={{ borderColor: "#9CA3AF" }}
                                            whileFocus={{ borderColor: "#FFFFFF", transition: { duration: 0.3 } }}
                                        ></motion.textarea>
                                    ) : (
                                        <motion.input
                                            type={field === 'email' ? 'email' : 'text'}
                                            id={field}
                                            value={field === 'name' ? name : email}
                                            onChange={(e) => field === 'name' ? setName(e.target.value) : setEmail(e.target.value)}
                                            required
                                            className="w-full px-3 sm:px-4 py-2 bg-black text-white border-b border-gray-600 focus:outline-none focus:border-white"
                                            whileHover={{ borderColor: "#9CA3AF" }}
                                            whileFocus={{ borderColor: "#FFFFFF", transition: { duration: 0.3 } }}
                                        />
                                    )}
                                </motion.div>
                            ))}
                            <motion.button
                                type="submit"
                                disabled={submitting}
                                className="text-white border border-white px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base hover:bg-white hover:text-black transition-colors duration-300 relative overflow-hidden"
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.98 }}
                                variants={itemVariants}
                            >
                                {submitting ? (
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                        className="w-6 h-6 border-t-2 border-b-2 border-white rounded-full"
                                    />
                                ) : (
                                    'Submit'
                                )}
                            </motion.button>
                            <AnimatePresence>
                                {submitted && (
                                    <motion.p
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.4, ease: "easeInOut" }}
                                        className="text-green-500 mt-4 text-sm sm:text-base"
                                    >
                                        Thank you for your message! We will get back to you soon.
                                    </motion.p>
                                )}
                                {error && (
                                    <motion.p
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.4, ease: "easeInOut" }}
                                        className="text-red-500 mt-4 text-sm sm:text-base"
                                    >
                                        {error}
                                    </motion.p>
                                )}
                            </AnimatePresence>
                        </form>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default ContactPage;